/**
 * /change-password — in-app password rotation for logged-in users.
 *
 * Mirrors the design + validation of /reset-password but additionally
 * requires the current password (defends against drive-by hijacks from
 * a left-open browser session). Server runs the same NIST length + HIBP
 * breach checks as the reset flow.
 *
 * On success the server invalidates every other session for the user
 * and refreshes the cookies on THIS response, so the caller stays
 * logged in but any other devices are bounced.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { KeyRound, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import PasswordStrengthMeter from '../components/PasswordStrengthMeter';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MIN_LENGTH = 10;

const ChangePassword = () => {
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // Client-side guards just for instant feedback — server is the gate.
  const lengthOk = newPassword.length >= MIN_LENGTH;
  const lengthCapped = newPassword.length <= 128;
  const match = newPassword === confirm;
  const different = currentPassword !== newPassword;
  const canSubmit = currentPassword && lengthOk && lengthCapped && match && different && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/auth/me/change-password`,
        { current_password: currentPassword, new_password: newPassword },
        { withCredentials: true },
      );
      setDone(true);
      toast.success('Password updated. Other sessions have been signed out.');
      setTimeout(() => navigate('/dashboard'), 1800);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail
        : Array.isArray(detail) ? (detail[0]?.msg || 'Could not update your password.')
        : 'Could not update your password.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 sm:p-8 max-w-2xl mx-auto" data-testid="change-password-page">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="text-sm text-[#64748B] hover:text-[#020617] mb-6"
        data-testid="change-password-back"
      >
        ← Back
      </button>

      {done ? (
        <div className="bg-white border border-[#E2E8F0] rounded-2xl p-10 shadow-sm text-center" data-testid="change-password-success">
          <CheckCircle2 className="w-12 h-12 text-[#047857] mx-auto mb-4" />
          <h1 className="text-2xl font-extrabold text-[#020617] mb-2">Password updated.</h1>
          <p className="text-[#64748B]">Taking you back to your dashboard…</p>
        </div>
      ) : (
        <>
          <div className="w-11 h-11 rounded-xl bg-[#EFF6FF] flex items-center justify-center mb-4">
            <KeyRound className="w-6 h-6 text-[#0052FF]" />
          </div>
          <h1 className="text-4xl tracking-tighter font-extrabold text-[#020617] mb-2" data-testid="change-password-title">
            Change your password
          </h1>
          <p className="text-base text-[#64748B] mb-8">
            At least {MIN_LENGTH} characters. We&apos;ll check the new password against
            known breach lists — no symbol or number rules. Changing your password signs
            you out of every other device.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5 bg-white border border-[#E2E8F0] rounded-2xl p-6 shadow-sm" data-testid="change-password-form">
            <div>
              <Label htmlFor="current-password" className="text-sm font-medium text-[#020617]">Current password</Label>
              <Input
                id="current-password"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                maxLength={128}
                className="mt-1.5 bg-white border border-[#E2E8F0] rounded-lg px-4 py-2 text-[#020617] focus:border-[#0052FF] focus:ring-1 focus:ring-[#0052FF]"
                placeholder="The password you use today"
                autoComplete="current-password"
                data-testid="change-password-current-input"
              />
            </div>

            <div>
              <Label htmlFor="new-password" className="text-sm font-medium text-[#020617]">New password</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={MIN_LENGTH}
                maxLength={128}
                className="mt-1.5 bg-white border border-[#E2E8F0] rounded-lg px-4 py-2 text-[#020617] focus:border-[#0052FF] focus:ring-1 focus:ring-[#0052FF]"
                placeholder="At least 10 characters"
                autoComplete="new-password"
                data-testid="change-password-new-input"
              />
              <PasswordStrengthMeter password={newPassword} minLength={MIN_LENGTH} />
              {newPassword && !different && (
                <p className="text-xs text-[#DC2626] mt-1.5" data-testid="change-password-same-as-current">
                  Your new password must be different from your current one.
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="confirm-password" className="text-sm font-medium text-[#020617]">Confirm new password</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                className="mt-1.5 bg-white border border-[#E2E8F0] rounded-lg px-4 py-2 text-[#020617] focus:border-[#0052FF] focus:ring-1 focus:ring-[#0052FF]"
                placeholder="Type it again"
                autoComplete="new-password"
                data-testid="change-password-confirm-input"
              />
              {confirm && !match && (
                <p className="text-xs text-[#DC2626] mt-1.5" data-testid="change-password-mismatch">
                  Passwords don&apos;t match.
                </p>
              )}
            </div>

            <Button
              type="submit"
              disabled={!canSubmit}
              className="w-full bg-[#0052FF] text-white rounded-lg px-6 py-2.5 font-medium hover:bg-[#003ECC] transition-colors disabled:opacity-60"
              data-testid="change-password-submit-button"
            >
              {submitting ? 'Updating…' : 'Update password'}
            </Button>
          </form>
        </>
      )}
    </div>
  );
};

export default ChangePassword;
